'use client';

import { Check, Copy, Heart, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

const Countdown = dynamic(() => import('./Countdown'), {
  ssr: false,
  loading: () => null,
});

/**
 * Mother's Day Countdown Banner
 *
 * Displays a sticky promo banner above the header with:
 *  - Live countdown to Mother's Day (May 10, 2026)
 *  - Coupon code with one-click copy-to-clipboard
 *  - Dismissible (persisted in localStorage)
 *  - Auto-hides after the campaign ends
 *
 * Design notes vs. tazeçiçek:
 *  - Cleaner typography hierarchy (campaign title > subtitle)
 *  - Smooth tick animation on second changes
 *  - Whole coupon pill is clickable (better mobile UX)
 *  - Visual feedback on copy (icon + label change)
 *  - Subtle gradient + heart accent for warmth
 *  - Properly responsive: stacked on mobile, single-row on desktop
 */

// ── Campaign Config ──────────────────────────────────────────────────────
const CAMPAIGN_CONFIG = {
  // Mother's Day 2026 = 10 May 2026 (2nd Sunday of May), 09:00 Turkey time
  endDate: new Date('2026-05-10T09:00:00+03:00'),
  title: 'Anneler Günü’ne 200₺ App İndirimi',
  subtitle: "App'ten vereceğin erken siparişte 200₺ indirim!",
  couponCode: 'ERKEN200',
  storageKey: 'hzc_mother_day_banner_dismissed_2026',
};

// ── Helpers ──────────────────────────────────────────────────────────────
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

function getTimeLeft(target: Date): TimeLeft {
  const now = Date.now();
  const total = target.getTime() - now;

  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((total / (1000 * 60)) % 60);
  const seconds = Math.floor((total / 1000) % 60);

  return { days, hours, minutes, seconds, total };
}

// ── Main Banner ──────────────────────────────────────────────────────────
export default function MotherDayCountdownBanner() {
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(CAMPAIGN_CONFIG.endDate));

  // Mount + read dismissed state from localStorage
  useEffect(() => {
    setMounted(true);
    try {
      const stored = localStorage.getItem(CAMPAIGN_CONFIG.storageKey);
      if (stored === '1') setDismissed(true);
    } catch {
      // ignore
    }
  }, []);

  // Tick countdown every second
  useEffect(() => {
    if (!mounted || dismissed) return;

    // We only need a coarse tick to know when to auto-hide; the visual
    // Countdown component does its own per-second ticking internally.
    const tick = () => {
      const next = getTimeLeft(CAMPAIGN_CONFIG.endDate);
      setTimeLeft(next);
      if (next.total <= 0) setDismissed(true);
    };
    tick();
    const interval = setInterval(tick, 30_000);
    return () => clearInterval(interval);
  }, [mounted, dismissed]);

  // Reset copied feedback after 2s
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CAMPAIGN_CONFIG.couponCode);
      setCopied(true);
    } catch {
      // Fallback for older browsers / app webviews without clipboard API
      try {
        const ta = document.createElement('textarea');
        ta.value = CAMPAIGN_CONFIG.couponCode;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
      } catch {
        // give up silently
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(CAMPAIGN_CONFIG.storageKey, '1');
    } catch {
      // ignore
    }
  };

  // SSR guard + hidden states
  if (!mounted) return null;
  if (dismissed) return null;
  if (timeLeft.total <= 0) return null;

  return (
    <div
      role="region"
      aria-label="Anneler Günü kampanya banner"
      className="relative w-full bg-gradient-to-r from-[#FDE9DC] via-[#FCD9CE] to-[#F9C5C5] text-hzc_purple_600 border-b border-hzc_purple_600/10"
    >
      <div className="w-full max-w-7xl mx-auto md:px-6 lg:px-0 pt-4 pb-0 md:pb-4 md:py-2.5">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4 w-full md:px-4 2xl:px-0 px-4 md:px-0">
          {/* Mobile layout: 2-column compact */}
          <div className="relative flex items-center gap-2 md:hidden w-full">
            {/* Dismiss button - absolute top right */}
            <button
              type="button"
              onClick={handleDismiss}
              className="absolute -top-2 -right-2 text-hzc_purple_600/50 hover:text-hzc_purple_600 transition-colors p-1 rounded-md hover:bg-white/40"
              aria-label="Banner'ı kapat"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Left: Text + Countdown */}
            <div className="flex flex-col gap-1.5 flex-1">
              <div className="text-sm font-bold leading-tight text-hzc_purple_600">
                {CAMPAIGN_CONFIG.title}
              </div>
              <div className="text-xs text-hzc_purple_600/75 leading-tight">
                {CAMPAIGN_CONFIG.subtitle}
              </div>
              <div className="flex items-start justify-start">
                <div className="flex items-start justify-start w-[220px] h-[48px] overflow-visible">
                  <Countdown
                    endDate={CAMPAIGN_CONFIG.endDate}
                    scale="0.35"
                    transformOrigin="top left"
                    cardColor="#FFFFFF"
                    numberColor="#222866"
                    textColor="#222866"
                    hideHeader={true}
                  />
                </div>
                {/* Right: Coupon code */}
                <div
                  className="flex items-center flex-shrink-0 justify-end"
                  style={{ padding: '4px 20px' }}
                >
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="group flex items-center gap-1 bg-hzc_purple_500 hover:bg-hzc_purple_600 active:scale-[0.98] transition-all text-white font-bold text-[11px] px-2 py-1.5 rounded-md shadow-sm"
                    aria-label={`İndirim kodunu kopyala: ${CAMPAIGN_CONFIG.couponCode}`}
                  >
                    <span className="tracking-wider">{CAMPAIGN_CONFIG.couponCode}</span>
                    <span className="flex items-center gap-1 border-l border-white/25 pl-1 ml-0.5">
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop layout: 3-column */}
          <div className="hidden md:flex items-center gap-2 sm:gap-3 w-full">
            {/* Left: Heart icon + Title + Subtitle */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-start">
              <div className="hidden sm:flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/60 flex-shrink-0 shadow-sm">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-rose-500" fill="currentColor" />
              </div>
              <div className="text-left whitespace-nowrap">
                <div className="text-[13px] sm:text-sm font-bold leading-tight text-hzc_purple_600">
                  {CAMPAIGN_CONFIG.title}
                </div>
                <div className="text-[11px] sm:text-xs text-hzc_purple_600/75 leading-tight mt-0.5">
                  {CAMPAIGN_CONFIG.subtitle}
                </div>
              </div>
            </div>

            {/* Center: Countdown */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 justify-center">
              <span className="hidden lg:inline text-[11px] uppercase tracking-wider text-hzc_purple_600/70 font-semibold">
                Anneler Gününe
              </span>
              <div className="flex items-center justify-center w-[120px] sm:w-[230px] h-[50px] sm:h-[60px] overflow-hidden">
                <Countdown
                  endDate={CAMPAIGN_CONFIG.endDate}
                  scale="0.28"
                  transformOrigin="center"
                  cardColor="#FFFFFF"
                  numberColor="#222866"
                  textColor="#222866"
                  hideHeader={true}
                />
              </div>
            </div>

            {/* Right: Coupon code + copy + dismiss */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              <div className="hidden lg:flex flex-col items-end mr-1">
                <span className="text-[10px] uppercase tracking-wider text-hzc_purple_600/70 font-semibold leading-none">
                  İndirim Kodu
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="group flex items-center gap-1.5 bg-hzc_purple_500 hover:bg-hzc_purple_600 active:scale-[0.98] transition-all text-white font-bold text-xs sm:text-sm px-3 py-2 rounded-md shadow-sm"
                aria-label={`İndirim kodunu kopyala: ${CAMPAIGN_CONFIG.couponCode}`}
              >
                <span className="tracking-wider">{CAMPAIGN_CONFIG.couponCode}</span>
                <span className="flex items-center gap-1 border-l border-white/25 pl-1.5 ml-0.5">
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span className="text-[11px]">Kopyalandı</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span className="text-[11px] hidden sm:inline">Kopyala</span>
                    </>
                  )}
                </span>
              </button>

              {/* Dismiss button */}
              <button
                type="button"
                onClick={handleDismiss}
                className="text-hzc_purple_600/50 hover:text-hzc_purple_600 transition-colors p-1 rounded-md hover:bg-white/40"
                aria-label="Banner'ı kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
